import { useEffect, useRef } from 'react';

// Module-level registry — survives renders but cleared on full reload.
// Each entry: { id, isDirty, message }
const registry = new Map();

/**
 * Track whether a form has unsaved changes. While `isDirty` is true:
 *  - the browser will prompt before closing the tab (`beforeunload`)
 *  - in-app navigation can call `confirmNavigation()` to ask the user
 *
 * Usage in a form:
 *   const dirty = isModified;
 *   useDirtyForm(dirty, 'Perubahan invoice belum disimpan. Lanjutkan?');
 */
export function useDirtyForm(isDirty, message = 'Perubahan belum disimpan. Yakin ingin meninggalkan halaman?') {
  const idRef = useRef(`df-${Math.random().toString(36).slice(2)}`);

  useEffect(() => {
    const id = idRef.current;
    if (isDirty) {
      registry.set(id, { isDirty: true, message });
    } else {
      registry.delete(id);
    }
    return () => { registry.delete(id); };
  }, [isDirty, message]);
}

// Used at app init exactly once.
let beforeUnloadInstalled = false;
export function installBeforeUnloadGuard() {
  if (beforeUnloadInstalled || typeof window === 'undefined') return;
  beforeUnloadInstalled = true;
  window.addEventListener('beforeunload', (e) => {
    for (const entry of registry.values()) {
      if (entry.isDirty) {
        e.preventDefault();
        e.returnValue = entry.message;
        return entry.message;
      }
    }
  });
}

/**
 * Returns true if user agrees (or no dirty form). Returns false if user cancels.
 * Use before in-app navigation away from a form. Async because the themed
 * confirm modal resolves via a Promise.
 */
export async function confirmNavigation() {
  for (const entry of registry.values()) {
    if (entry.isDirty) {
      let ok;
      try {
        // Use themed app confirm if available
        const mod = await import('../components/shared/AppConfirm');
        ok = await mod.showAppConfirm(entry.message);
      } catch {
        // eslint-disable-next-line no-alert
        ok = window.confirm(entry.message);
      }
      if (!ok) return false;
      // User confirmed → clear all dirty flags so subsequent nav is silent
      registry.clear();
      return true;
    }
  }
  return true;
}
